import {BrowserRouter as Router, Navigate, Route, Routes} from "react-router-dom";
import {Box, Container, Drawer} from "@mui/material";
import {ReactElement, useEffect, useState} from "react";
import Navbar from "./components/Navbar";
import SideMenu from "./components/SideMenu";
import Footer from "./components/Footer";
import CoursesPage from "./pages/CoursesPage";
import CourseDetailsPage from "./pages/CourseDetailsPage";
import LessonDetailsPage from "./pages/LessonDetailsPage";
import ChapterDetailsPage from "./pages/ChapterDetailsPage";
import Login from "./pages/LoginPage";
import ProfilePage from "./pages/ProfilePage";
import ForbiddenPage from "./pages/ForbiddenPage";
import UsersPage from "./pages/UsersPage";
import AdminCodeRunnerPage from "./pages/AdminCodeRunnerPage";
import CodeTaskDetailsPage from "./pages/CodeTaskDetailsPage";
import HomeRoute from "./routes/HomeRoute";
import {AlertProvider} from "./context/AlertContext";
import {AuthProvider, useAuth} from "./context/AuthContext";
import MyCoursesPage from "./pages/MyCoursesPage";
import MyCourseDetailsPage from "./pages/MyCourseDetailsPage";
import MyChapterDetailsPage from "./pages/MyChapterDetailsPage";
import MyLessonDetailsPage from "./pages/MyLessonDetailsPage";
import MyTasksPage from "./pages/MyTasksPage";
import MyCodeTaskDetailsPage from "./pages/MyCodeTaskDetailsPage";
import TasksPage from "./pages/TasksPage";
import {ColorModeProvider} from "./context/ColorModeContext";
import CourseAccessPage from "./pages/CourseAccessPage";
import CourseAccessDetailsPage from "./pages/CourseAccessDetailsPage";
import PracticeTasksPage from "./pages/PracticeTasksPage";
import PracticeTaskDetailsPage from "./pages/PracticeTaskDetailsPage";
import MyPracticeTasksPage from "./pages/MyPracticeTasksPage";
import MyPracticeTaskDetailsPage from "./pages/MyPracticeTaskDetailsPage";
import AdminStreamsPage from "./pages/AdminStreamsPage";
import AdminStreamUsersDetailsPage from "./pages/AdminStreamUsersDetailsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import VerifyOtpPage from "./pages/VerifyOtpPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RoleRoute from "./routes/RoleRoute";
import MyStreamsPage from "./pages/MyStreamsPage";
import MyStreamUsersDetailsPage from "./pages/MyStreamUsersDetailsPage";
import AdminCodeTaskAttemptsPage from "./pages/AdminCodeTaskAttemptsPage";
import TeacherCodeTaskAttemptsPage from "./pages/TeacherCodeTaskAttemptsPage";
import AdminStreamDetailsPage from "./pages/AdminStreamDetailsPage";
import MyStreamDetailsPage from "./pages/MyStreamDetailsPage";
import MySchedulePage from "./pages/MySchedulePage";
import AdminUserAnalyticsPage from "./pages/AdminUserAnalyticsPage";
import AdminUserAttemptsPage from "./components/AdminUserAttemptsPage";
import LiveTestPage from "./components/LiveTestPage";
import TeacherUserAnalyticsPage from "./pages/TeacherUserAnalyticsPage";
import TeacherUserAttemptsPage from "./pages/TeacherUserAttemptsPage";
import ScrollToTopButton from "./components/ScrollToTopButton";
import AdminCompaniesPage from "./pages/AdminCompaniesPage";
import AdminCompanyDetailsPage from "./pages/AdminCompanyDetailsPage";
import AdminCourseLogosPage from "./pages/AdminCourseLogosPage";
import JavaPlaygroundTestPage from "./pages/JavaPlaygroundTestPage";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "bitlab_lms_sidebar_collapsed";

const getInitialSidebarCollapsed = () => {
    try {
        return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
        return false;
    }
};

const PrivateRoute = ({element}: { element: ReactElement }) => {
    const {user, isLoading} = useAuth();

    if (isLoading) return null;
    return user ? element : <Navigate to="/login" replace/>;
};

const AppContent = () => {
    const {user, isLoading} = useAuth();
    const isLoggedIn = !!user;

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialSidebarCollapsed);

    const closeMobileMenu = () => setMobileMenuOpen(false);

    useEffect(() => {
        if (!isLoggedIn) setMobileMenuOpen(false);
    }, [isLoggedIn]);

    useEffect(() => {
        try {
            window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, String(sidebarCollapsed));
        } catch {
            // ignore localStorage errors
        }
    }, [sidebarCollapsed]);

    return (
        <Router>
            <Box sx={{minHeight: "100vh", display: "flex", flexDirection: "column"}}>
                <Navbar onOpenMenu={() => setMobileMenuOpen(true)}/>

                {!isLoading && isLoggedIn && (
                    <Drawer
                        open={mobileMenuOpen}
                        onClose={closeMobileMenu}
                        slotProps={{
                            paper: {
                                sx: {width: 280},
                            },
                        }}
                    >
                        <Box sx={{p: 2}}>
                            <SideMenu onNavigate={closeMobileMenu}/>
                        </Box>
                    </Drawer>
                )}

                <Box
                    sx={{
                        flex: 1,
                        bgcolor: "background.default",
                        py: 3,
                    }}
                >
                    <Container maxWidth="xl">
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 3,
                                width: "100%",
                            }}
                        >
                            {!isLoading && isLoggedIn && (
                                <Box
                                    sx={(theme) => ({
                                        display: {xs: "none", md: "block"},
                                        width: sidebarCollapsed ? 72 : 280,
                                        flexShrink: 0,
                                        overflow: "hidden",
                                        transition: theme.transitions.create(["width"], {
                                            duration: theme.transitions.duration.standard,
                                            easing: theme.transitions.easing.easeInOut,
                                        }),
                                    })}
                                >
                                    <Box
                                        sx={{
                                            position: "sticky",
                                            top: 88,
                                        }}
                                    >
                                        <Box
                                            sx={(theme) => ({
                                                p: sidebarCollapsed ? 1 : 2,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 3,
                                                bgcolor: "background.paper",
                                                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                                                transition: theme.transitions.create(["padding"], {
                                                    duration: theme.transitions.duration.standard,
                                                    easing: theme.transitions.easing.easeInOut,
                                                }),
                                            })}
                                        >
                                            <SideMenu
                                                collapsed={sidebarCollapsed}
                                                onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            <Box
                                sx={
                                    !isLoading && !isLoggedIn
                                        ? {
                                            width: "100%",
                                            maxWidth: 900,
                                            mx: "auto",
                                        }
                                        : {
                                            flex: 1,
                                            minWidth: 0,
                                            transition: "all 220ms ease",
                                        }
                                }
                            >
                                <Box
                                    sx={(theme) => ({
                                        border: !isLoading && isLoggedIn ? `1px solid ${theme.palette.divider}` : "none",
                                        borderRadius: 3,
                                        bgcolor: !isLoading && isLoggedIn ? "background.paper" : "transparent",
                                        boxShadow: !isLoading && isLoggedIn ? "0 1px 2px rgba(15,23,42,0.04)" : "none",
                                        py: !isLoading && isLoggedIn ? {xs: 2, md: 3} : 0,
                                    })}
                                >
                                    <Routes>
                                        <Route path="/login" element={<Login/>}/>
                                        <Route path="/forbidden" element={<ForbiddenPage/>}/>
                                        <Route path="/" element={<HomeRoute/>}/>

                                        <Route path="/forgot-password" element={<ForgotPasswordPage/>}/>
                                        <Route path="/verify-otp" element={<VerifyOtpPage/>}/>
                                        <Route path="/reset-password" element={<ResetPasswordPage/>}/>
                                        <Route path="/java-playground-test" element={<JavaPlaygroundTestPage/>}/>

                                        <Route path="/profile" element={<PrivateRoute element={<ProfilePage/>}/>}/>

                                        <Route
                                            path="/courses"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<CoursesPage/>}/>}
                                        />
                                        <Route
                                            path="/course/:courseId"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<CourseDetailsPage/>}/>}
                                        />
                                        <Route
                                            path="/chapter/:chapterId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<ChapterDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/lesson/:lessonId"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<LessonDetailsPage/>}/>}
                                        />
                                        <Route
                                            path="/users"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<UsersPage/>}/>}
                                        />
                                        <Route
                                            path="/admin/code-runner"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminCodeRunnerPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/code-tasks/:taskId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<CodeTaskDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/lesson/:lessonId/tasks"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<TasksPage/>}/>}
                                        />
                                        <Route
                                            path="/lesson/:lessonId/practices"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<PracticeTasksPage/>}/>}
                                        />
                                        <Route
                                            path="/practice-tasks/:taskId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<PracticeTaskDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/course-access"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<CourseAccessPage/>}/>}
                                        />
                                        <Route
                                            path="/course-access/:courseId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<CourseAccessDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/admin/streams"
                                            element={<RoleRoute roles={["ROLE_ADMIN"]} element={<AdminStreamsPage/>}/>}
                                        />
                                        <Route
                                            path="/admin/streams/:streamId/users"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminStreamUsersDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/admin/code-tasks/:taskId/attempts"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminCodeTaskAttemptsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/admin/streams/:streamId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminStreamDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/admin/users/:userId/analytics"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminUserAnalyticsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/admin/users/:userId/attempts"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminUserAttemptsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/live-test"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<LiveTestPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/companies"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminCompaniesPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/companies/:companyId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminCompanyDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/course-logos"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_ADMIN"]}
                                                    element={<AdminCourseLogosPage/>}
                                                />
                                            }
                                        />

                                        {/* STUDENT, TEACHER */}
                                        <Route
                                            path="/my-courses"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyCoursesPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/course-details/:courseId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyCourseDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/chapter-details/:chapterId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyChapterDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/lesson-details/:lessonId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyLessonDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/lesson-details/:lessonId/tasks"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyTasksPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/task-details/:taskId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyCodeTaskDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/lesson-details/:lessonId/practices"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyPracticeTasksPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/my-practice-task-details/:taskId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyPracticeTaskDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/my-streams"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyStreamsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/my-streams/:streamId/users"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_TEACHER", "ROLE_ADMIN"]}
                                                    element={<MyStreamUsersDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/my-streams/:streamId"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MyStreamDetailsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/my-schedule"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_STUDENT", "ROLE_TEACHER"]}
                                                    element={<MySchedulePage/>}
                                                />
                                            }
                                        />

                                        {/* TEACHER ONLY */}
                                        <Route
                                            path="/teacher/code-tasks/:taskId/attempts"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_TEACHER"]}
                                                    element={<TeacherCodeTaskAttemptsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/teacher/users/:userId/analytics"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_TEACHER"]}
                                                    element={<TeacherUserAnalyticsPage/>}
                                                />
                                            }
                                        />
                                        <Route
                                            path="/teacher/users/:userId/attempts"
                                            element={
                                                <RoleRoute
                                                    roles={["ROLE_TEACHER"]}
                                                    element={<TeacherUserAttemptsPage/>}
                                                />
                                            }
                                        />

                                        <Route path="*" element={<Navigate to="/" replace/>}/>
                                    </Routes>
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>

                <ScrollToTopButton/>
                <Footer/>
            </Box>
        </Router>
    );
};

const App = () => (
    <AlertProvider>
        <AuthProvider>
            <ColorModeProvider>
                <AppContent/>
            </ColorModeProvider>
        </AuthProvider>
    </AlertProvider>
);

export default App;